/**
 * DecisionFlow — ContestManager
 * ui/src/components/tenant-admin/ContestManager.jsx
 *
 * Domain: §P4-10 Contests
 * Nav route: /admin/contests
 * API:
 *   GET  /api/v1/contests?fiscal_period_id={id}
 *   POST /api/v1/contests
 *   PATCH /api/v1/contests/{id}          (status transition)
 *   POST /api/v1/contests/{id}/approve   (NN-10; must be 'open' status)
 *   GET  /api/v1/contest-prizes?contest_id={id}
 *   POST /api/v1/contest-prizes
 *   GET  /api/v1/contest-leaderboard?contest_id={id}    (read-only)
 *   GET  /api/v1/contest-award-results?contest_id={id}  (read-only)
 * Feature gate: Standard — no gate.
 * Tabs: Contests | Prizes | Leaderboard | Award Results
 * Status machine: draft → open → active → closed → awarded (forward-only)
 * State: contest_status column directly — NOT derived from approved_by.
 * Approve action only valid when contest_status === 'open'.
 * Path: ui/src/components/tenant-admin/ContestManager.jsx
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useFeatures } from '../../auth/FeatureContext.jsx';
import { apiGet, apiPost } from '../../api/client';
import ApprovalPanel from '../shared/ApprovalPanel.jsx';
import GateStatusBanner from '../shared/GateStatusBanner.jsx';
import StatusPill from '../shared/StatusPill.jsx';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getApiErrorCode(error) { return error?.code || null; }
function getErrorMessage(error, fallback) { return error?.message || fallback; }

// ---------------------------------------------------------------------------
// CSS tokens
// ---------------------------------------------------------------------------

const thClass      = 'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]';
const tdClass      = 'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] border-b border-[rgb(var(--color-border))]';
const btnPrimary   = 'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50 disabled:cursor-not-allowed';
const btnSecondary = 'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50 disabled:cursor-not-allowed';
const inputClass   = 'mt-1 block w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-focus-ring))]';
const labelClass   = 'flex flex-col text-tok-sm font-medium text-[rgb(var(--color-text))]';

// ---------------------------------------------------------------------------
// Status machine
// ---------------------------------------------------------------------------

const CONTEST_STATES = ['draft', 'open', 'active', 'closed', 'awarded'];

const STATUS_TRANSITIONS = {
  draft:   'open',
  open:    'active',
  active:  'closed',
  closed:  'awarded',
  awarded: null, // terminal
};

const STATUS_STYLE = {
  draft:   'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]',
  open:    'bg-[rgb(var(--color-info))]/10 text-[rgb(var(--color-info))]',
  active:  'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))]',
  closed:  'bg-[rgb(var(--color-warning))]/10 text-[rgb(var(--color-warning))]',
  awarded: 'bg-[rgb(var(--color-primary))]/10 text-[rgb(var(--color-primary))]',
};

const TRANSITION_LABELS = {
  draft:  'Open Contest',
  open:   'Activate',
  active: 'Close Contest',
  closed: 'Mark Awarded',
};

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

const TABS = [
  { key: 'contests',      label: 'Contests'       },
  { key: 'prizes',        label: 'Prizes'         },
  { key: 'leaderboard',   label: 'Leaderboard'    },
  { key: 'award-results', label: 'Award Results'  },
];

// ---------------------------------------------------------------------------
// Form defaults
// ---------------------------------------------------------------------------

const EMPTY_CONTEST = {
  contest_name:  '',
  description:   '',
  start_date:    '',
  end_date:      '',
  metric:        '',
};

const EMPTY_PRIZE = {
  prize_rank:   '',
  prize_name:   '',
  prize_value:  '',
  currency:     'USD',
  description:  '',
};

// ---------------------------------------------------------------------------
// ContestStatusBadge
// ---------------------------------------------------------------------------

function ContestStatusBadge({ status }) {
  const s = status ?? 'draft';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-tok-xs font-medium ${STATUS_STYLE[s] ?? STATUS_STYLE.draft}`}>
      {s.charAt(0).toUpperCase() + s.slice(1)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// StatusTimeline — forward-only visual machine
// ---------------------------------------------------------------------------

function StatusTimeline({ currentStatus }) {
  const idx = CONTEST_STATES.indexOf(currentStatus ?? 'draft');
  return (
    <div className="flex items-center gap-0 overflow-x-auto">
      {CONTEST_STATES.map((s, i) => {
        const done    = i < idx;
        const current = i === idx;
        return (
          <React.Fragment key={s}>
            <div className={`flex flex-col items-center gap-0.5 min-w-[64px] ${current ? 'opacity-100' : done ? 'opacity-60' : 'opacity-30'}`}>
              <div className={`h-3 w-3 rounded-full border-2 ${current ? 'border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))]' : done ? 'border-[rgb(var(--color-success))] bg-[rgb(var(--color-success))]' : 'border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))]'}`} />
              <span className={`text-tok-xs font-medium ${current ? 'text-[rgb(var(--color-primary))]' : done ? 'text-[rgb(var(--color-success))]' : 'text-[rgb(var(--color-text-muted))]'}`}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </span>
            </div>
            {i < CONTEST_STATES.length - 1 && (
              <div className={`h-px flex-1 min-w-[16px] ${i < idx ? 'bg-[rgb(var(--color-success))]' : 'bg-[rgb(var(--color-border))]'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ContestManager
// ---------------------------------------------------------------------------

export default function ContestManager({
  mode, period, wizardSteps, embedded = false,
  planId, planComponentId, fiscalPeriodId, readOnly = false,
}) {
  const { user } = useAuth();
  const { hasPlanType } = useFeatures();

  if (!hasPlanType('feature.plan.contest')) {
    return <GateStatusBanner code="PLAN_TYPE_NOT_ENTITLED" />;
  }
  const fpId = period?.fiscal_period_id ?? fiscalPeriodId ?? null;

  const [activeTab, setActiveTab] = useState('contests');

  // ── Contests ──
  const [contests, setContests]             = useState([]);
  const [contestsLoading, setContestsLoading] = useState(false);
  const [contestsError, setContestsError]   = useState('');
  const [selectedContest, setSelectedContest] = useState(null);
  const [showContestForm, setShowContestForm] = useState(false);
  const [contestForm, setContestForm]       = useState(EMPTY_CONTEST);
  const [contestSaving, setContestSaving]   = useState(false);
  const [contestFormError, setContestFormError] = useState('');
  const [transitionLoading, setTransitionLoading] = useState(false);
  const [transitionError, setTransitionError]     = useState('');
  const [approveLoading, setApproveLoading] = useState(false);
  const [approveError, setApproveError]     = useState('');

  // ── Prizes ──
  const [prizes, setPrizes]                 = useState([]);
  const [prizesLoading, setPrizesLoading]   = useState(false);
  const [prizesError, setPrizesError]       = useState('');
  const [showPrizeForm, setShowPrizeForm]   = useState(false);
  const [prizeForm, setPrizeForm]           = useState(EMPTY_PRIZE);
  const [prizeSaving, setPrizeSaving]       = useState(false);
  const [prizeFormError, setPrizeFormError] = useState('');

  // ── Leaderboard ──
  const [leaderboard, setLeaderboard]       = useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [leaderboardError, setLeaderboardError]     = useState('');

  // ── Award Results ──
  const [awardResults, setAwardResults]     = useState([]);
  const [awardResultsLoading, setAwardResultsLoading] = useState(false);
  const [awardResultsError, setAwardResultsError]     = useState('');

  const [toast, setToast] = useState(null);
  const showToast = useCallback((msg) => { setToast(msg); window.setTimeout(() => setToast(null), 3000); }, []);

  // ---------------------------------------------------------------------------
  // Load contests
  // ---------------------------------------------------------------------------

  const loadContests = useCallback(async () => {
    if (!user) return;
    setContestsLoading(true); setContestsError('');
    try {
      const url = fpId
        ? `/api/v1/contests?fiscal_period_id=${fpId}`
        : '/api/v1/contests';
      const data = await apiGet(url);
      setContests(Array.isArray(data) ? data : data?.items || data?.data || []);
    } catch (e) { setContestsError(getErrorMessage(e, 'Unable to load contests.')); }
    finally { setContestsLoading(false); }
  }, [user, fpId]);

  useEffect(() => { loadContests(); }, [loadContests]);

  // ---------------------------------------------------------------------------
  // Load sub-lists on tab switch
  // ---------------------------------------------------------------------------

  const loadPrizes = useCallback(async (contestId) => {
    if (!contestId) return;
    setPrizesLoading(true); setPrizesError('');
    try {
      const data = await apiGet(`/api/v1/contest-prizes?contest_id=${contestId}`);
      setPrizes(Array.isArray(data) ? data : data?.items || data?.data || []);
    } catch (e) { setPrizesError(getErrorMessage(e, 'Unable to load prizes.')); }
    finally { setPrizesLoading(false); }
  }, []);

  const loadLeaderboard = useCallback(async (contestId) => {
    if (!contestId) return;
    setLeaderboardLoading(true); setLeaderboardError('');
    try {
      const data = await apiGet(`/api/v1/contest-leaderboard?contest_id=${contestId}`);
      setLeaderboard(Array.isArray(data) ? data : data?.items || data?.data || []);
    } catch (e) { setLeaderboardError(getErrorMessage(e, 'Unable to load leaderboard.')); }
    finally { setLeaderboardLoading(false); }
  }, []);

  const loadAwardResults = useCallback(async (contestId) => {
    if (!contestId) return;
    setAwardResultsLoading(true); setAwardResultsError('');
    try {
      const data = await apiGet(`/api/v1/contest-award-results?contest_id=${contestId}`);
      setAwardResults(Array.isArray(data) ? data : data?.items || data?.data || []);
    } catch (e) { setAwardResultsError(getErrorMessage(e, 'Unable to load award results.')); }
    finally { setAwardResultsLoading(false); }
  }, []);

  useEffect(() => {
    if (!selectedContest) return;
    const id = selectedContest.contest_id;
    if (activeTab === 'prizes')        loadPrizes(id);
    if (activeTab === 'leaderboard')   loadLeaderboard(id);
    if (activeTab === 'award-results') loadAwardResults(id);
  }, [activeTab, selectedContest, loadPrizes, loadLeaderboard, loadAwardResults]);

  // ---------------------------------------------------------------------------
  // Contest create
  // ---------------------------------------------------------------------------

  async function handleCreateContest(e) {
    e.preventDefault();
    if (!contestForm.contest_name.trim()) { setContestFormError('Contest name is required.'); return; }
    setContestSaving(true); setContestFormError('');
    try {
      await apiPost('/api/v1/contests', {
        fiscal_period_id: fpId,
        contest_name:     contestForm.contest_name.trim(),
        description:      contestForm.description.trim() || null,
        start_date:       contestForm.start_date || null,
        end_date:         contestForm.end_date || null,
        metric:           contestForm.metric.trim() || null,
        contest_status:   'draft',
      });
      setShowContestForm(false); setContestForm(EMPTY_CONTEST);
      showToast('Contest created.');
      await loadContests();
    } catch (e) { setContestFormError(getErrorMessage(e, 'Failed to create contest.')); }
    finally { setContestSaving(false); }
  }

  // ---------------------------------------------------------------------------
  // Status transition (forward-only PATCH)
  // ---------------------------------------------------------------------------

  async function handleTransition() {
    if (!selectedContest) return;
    const nextStatus = STATUS_TRANSITIONS[selectedContest.contest_status];
    if (!nextStatus) return;
    setTransitionLoading(true); setTransitionError('');
    try {
      await apiPost(`/api/v1/contests/${selectedContest.contest_id}`, {
        _method: 'PATCH',
        contest_status: nextStatus,
      });
      showToast(`Contest moved to ${nextStatus}.`);
      await loadContests();
      setSelectedContest((c) => ({ ...c, contest_status: nextStatus }));
    } catch (e) { setTransitionError(getErrorMessage(e, 'Failed to update contest status.')); }
    finally { setTransitionLoading(false); }
  }

  // ---------------------------------------------------------------------------
  // Approve (NN-10; only when status === 'open')
  // ---------------------------------------------------------------------------

  async function handleApprove() {
    if (!selectedContest || selectedContest.contest_status !== 'open') return;
    setApproveLoading(true); setApproveError('');
    try {
      await apiPost(`/api/v1/contests/${selectedContest.contest_id}/approve`);
      showToast('Contest approved.');
      await loadContests();
      setSelectedContest((c) => ({ ...c, approved_by: user?.user_id || user?.id }));
    } catch (e) {
      const code = getApiErrorCode(e);
      setApproveError(code === 'SELF_APPROVAL_NOT_ALLOWED' ? 'Self-approval is not permitted (NN-10).' : getErrorMessage(e, 'Failed to approve.'));
    } finally { setApproveLoading(false); }
  }

  // ---------------------------------------------------------------------------
  // Prize create
  // ---------------------------------------------------------------------------

  async function handleCreatePrize(e) {
    e.preventDefault();
    if (!selectedContest) { setPrizeFormError('Select a contest first.'); return; }
    if (!prizeForm.prize_name.trim()) { setPrizeFormError('Prize name is required.'); return; }
    setPrizeSaving(true); setPrizeFormError('');
    try {
      await apiPost('/api/v1/contest-prizes', {
        contest_id:   selectedContest.contest_id,
        prize_rank:   prizeForm.prize_rank ? parseInt(prizeForm.prize_rank, 10) : null,
        prize_name:   prizeForm.prize_name.trim(),
        prize_value:  prizeForm.prize_value ? parseFloat(prizeForm.prize_value) : null,
        currency:     prizeForm.currency || 'USD',
        description:  prizeForm.description.trim() || null,
      });
      setShowPrizeForm(false); setPrizeForm(EMPTY_PRIZE);
      showToast('Prize added.');
      await loadPrizes(selectedContest.contest_id);
    } catch (e) { setPrizeFormError(getErrorMessage(e, 'Failed to add prize.')); }
    finally { setPrizeSaving(false); }
  }

  // ---------------------------------------------------------------------------
  // Guards
  // ---------------------------------------------------------------------------

  const currentUserId = user?.user_id || user?.id || null;

  if (!user) return (
    <section className="flex flex-col gap-4">
      <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">Authentication required.</div>
    </section>
  );

  function NoContestSelected() {
    return (
      <div className="rounded-lg border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/5 px-4 py-3 text-tok-sm text-[rgb(var(--color-warning))]">
        Select a contest in the Contests tab first.
      </div>
    );
  }

  function ReadOnlyNote() {
    return (
      <div className="rounded-lg bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text-muted))]">
        Results are engine-written and read-only.
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <section className="flex flex-col gap-4">
      <h1 className="text-tok-lg font-bold text-[rgb(var(--color-text))]">Contests</h1>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-[rgb(var(--color-border))] overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setActiveTab(t.key)}
            className={`whitespace-nowrap px-4 py-2 text-tok-sm font-medium border-b-2 transition ${
              activeTab === t.key
                ? 'border-[rgb(var(--color-primary))] text-[rgb(var(--color-primary))]'
                : 'border-transparent text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Contests tab ── */}
      {activeTab === 'contests' && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Contests</h2>
            {!readOnly && <button type="button" onClick={() => { setShowContestForm((v) => !v); setContestFormError(''); setContestForm(EMPTY_CONTEST); }} className={btnPrimary}>{showContestForm ? 'Cancel' : '+ New Contest'}</button>}
          </div>

          {showContestForm && (
            <form onSubmit={handleCreateContest} className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3">
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">New Contest</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className={labelClass + ' sm:col-span-2'}>
                  Contest Name <span className="text-[rgb(var(--color-danger))]">*</span>
                  <input type="text" value={contestForm.contest_name} onChange={(e) => setContestForm((f) => ({ ...f, contest_name: e.target.value }))} placeholder="e.g. Q2 Accelerator Challenge" className={inputClass} required />
                </label>
                <label className={labelClass}>Metric<input type="text" value={contestForm.metric} onChange={(e) => setContestForm((f) => ({ ...f, metric: e.target.value }))} placeholder="e.g. revenue, units" className={inputClass} /></label>
                <div />
                <label className={labelClass}>Start Date<input type="date" value={contestForm.start_date} onChange={(e) => setContestForm((f) => ({ ...f, start_date: e.target.value }))} className={inputClass} /></label>
                <label className={labelClass}>End Date<input type="date" value={contestForm.end_date} onChange={(e) => setContestForm((f) => ({ ...f, end_date: e.target.value }))} className={inputClass} /></label>
                <label className={labelClass + ' sm:col-span-2'}>Description<textarea value={contestForm.description} onChange={(e) => setContestForm((f) => ({ ...f, description: e.target.value }))} rows={2} className={inputClass} /></label>
              </div>
              {contestFormError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{contestFormError}</div>}
              <div className="flex gap-2"><button type="submit" disabled={contestSaving} className={btnPrimary}>{contestSaving ? 'Saving…' : 'Create Contest'}</button><button type="button" onClick={() => { setShowContestForm(false); setContestFormError(''); }} className={btnSecondary}>Cancel</button></div>
            </form>
          )}

          {contestsLoading && <div className="animate-pulse space-y-2"><div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" /><div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" /></div>}
          {contestsError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">{contestsError}</div>}
          {!contestsLoading && !contestsError && contests.length === 0 && (
            <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] px-6 py-8 text-center">
              <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">No contests</p>
              <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">Create a contest to run competitive incentive programs for the period.</p>
            </div>
          )}
          {contests.length > 0 && (
            <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
              <table className="min-w-full border-collapse">
                <thead>
                  <tr>
                    <th className={thClass}>Contest Name</th>
                    <th className={thClass}>Metric</th>
                    <th className={thClass}>Start</th>
                    <th className={thClass}>End</th>
                    <th className={thClass}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {contests.map((c) => (
                    <tr key={c.contest_id}
                      className={`cursor-pointer hover:bg-[rgb(var(--color-surface-muted))] transition-colors ${selectedContest?.contest_id === c.contest_id ? 'bg-[rgb(var(--color-primary))]/5' : ''}`}
                      onClick={() => { setSelectedContest(c); setTransitionError(''); setApproveError(''); }}
                    >
                      <td className={tdClass + ' font-medium'}>{c.contest_name}</td>
                      <td className={tdClass}>{c.metric ?? '—'}</td>
                      <td className={tdClass}>{c.start_date ?? '—'}</td>
                      <td className={tdClass}>{c.end_date ?? '—'}</td>
                      <td className={tdClass}><ContestStatusBadge status={c.contest_status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Selected contest — status machine + actions */}
          {selectedContest && !readOnly && (
            <div className="flex flex-col gap-4 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">{selectedContest.contest_name}</p>
                <ContestStatusBadge status={selectedContest.contest_status} />
              </div>

              {/* Timeline */}
              <StatusTimeline currentStatus={selectedContest.contest_status} />

              {/* Status transition */}
              {STATUS_TRANSITIONS[selectedContest.contest_status] && (
                <div className="flex flex-col gap-2">
                  {transitionError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{transitionError}</div>}
                  <button type="button" onClick={handleTransition} disabled={transitionLoading} className={btnSecondary + ' w-fit'}>
                    {transitionLoading ? 'Updating…' : TRANSITION_LABELS[selectedContest.contest_status]}
                  </button>
                </div>
              )}

              {/* Approve — only valid when status is 'open' */}
              {selectedContest.contest_status === 'open' && !selectedContest.approved_by && (
                <div className="flex flex-col gap-2 border-t border-[rgb(var(--color-border))] pt-3">
                  <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">Approve contest to lock configuration before activating.</p>
                  {approveError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{approveError}</div>}
                  <button type="button" onClick={handleApprove} disabled={approveLoading} className={btnPrimary + ' w-fit'}>
                    {approveLoading ? 'Approving…' : 'Approve Contest'}
                  </button>
                </div>
              )}
              {selectedContest.approved_by && (
                <p className="text-tok-xs text-[rgb(var(--color-success))]">Approved ✓</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Prizes tab ── */}
      {activeTab === 'prizes' && (
        <div className="flex flex-col gap-4">
          {!selectedContest ? <NoContestSelected /> : (
            <>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Prizes — {selectedContest.contest_name}</h2>
                {!readOnly && <button type="button" onClick={() => { setShowPrizeForm((v) => !v); setPrizeFormError(''); setPrizeForm(EMPTY_PRIZE); }} className={btnPrimary}>{showPrizeForm ? 'Cancel' : '+ Add Prize'}</button>}
              </div>

              {showPrizeForm && (
                <form onSubmit={handleCreatePrize} className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3">
                  <p className="text-tok-sm font-medium text-[rgb(var(--color-text))]">New Prize</p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label className={labelClass}>Rank<input type="number" min="1" step="1" value={prizeForm.prize_rank} onChange={(e) => setPrizeForm((f) => ({ ...f, prize_rank: e.target.value }))} placeholder="1" className={inputClass} /></label>
                    <label className={labelClass}>Prize Name <span className="text-[rgb(var(--color-danger))]">*</span><input type="text" value={prizeForm.prize_name} onChange={(e) => setPrizeForm((f) => ({ ...f, prize_name: e.target.value }))} placeholder="e.g. President's Award" className={inputClass} required /></label>
                    <label className={labelClass}>Prize Value<input type="number" step="0.01" min="0" value={prizeForm.prize_value} onChange={(e) => setPrizeForm((f) => ({ ...f, prize_value: e.target.value }))} placeholder="0.00" className={inputClass} /></label>
                    <label className={labelClass}>Currency<input type="text" maxLength={3} value={prizeForm.currency} onChange={(e) => setPrizeForm((f) => ({ ...f, currency: e.target.value.toUpperCase() }))} placeholder="USD" className={inputClass} /></label>
                    <label className={labelClass + ' sm:col-span-2'}>Description<textarea value={prizeForm.description} onChange={(e) => setPrizeForm((f) => ({ ...f, description: e.target.value }))} rows={2} className={inputClass} /></label>
                  </div>
                  {prizeFormError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">{prizeFormError}</div>}
                  <div className="flex gap-2"><button type="submit" disabled={prizeSaving} className={btnPrimary}>{prizeSaving ? 'Saving…' : 'Add Prize'}</button><button type="button" onClick={() => { setShowPrizeForm(false); setPrizeFormError(''); }} className={btnSecondary}>Cancel</button></div>
                </form>
              )}

              {prizesLoading && <div className="animate-pulse space-y-2"><div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" /></div>}
              {prizesError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">{prizesError}</div>}
              {!prizesLoading && !prizesError && prizes.length === 0 && (
                <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] px-6 py-8 text-center">
                  <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No prizes. Add prizes to define contest rewards by rank.</p>
                </div>
              )}
              {prizes.length > 0 && (
                <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
                  <table className="min-w-full border-collapse">
                    <thead>
                      <tr>
                        <th className={thClass}>Rank</th>
                        <th className={thClass}>Prize</th>
                        <th className={thClass}>Value</th>
                        <th className={thClass}>Currency</th>
                        <th className={thClass}>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {prizes.sort((a, b) => (a.prize_rank ?? 99) - (b.prize_rank ?? 99)).map((p) => (
                        <tr key={p.contest_prize_id} className="hover:bg-[rgb(var(--color-surface-muted))]">
                          <td className={tdClass + ' font-medium'}>{p.prize_rank != null ? `#${p.prize_rank}` : '—'}</td>
                          <td className={tdClass}>{p.prize_name}</td>
                          <td className={tdClass}>{p.prize_value != null ? Number(p.prize_value).toLocaleString() : '—'}</td>
                          <td className={tdClass}>{p.currency ?? '—'}</td>
                          <td className={tdClass + ' max-w-xs truncate text-[rgb(var(--color-text-muted))]'}>{p.description ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Leaderboard tab (read-only) ── */}
      {activeTab === 'leaderboard' && (
        <div className="flex flex-col gap-4">
          <ReadOnlyNote />
          {!selectedContest ? <NoContestSelected /> : (
            <>
              <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Leaderboard — {selectedContest.contest_name}</h2>
              {leaderboardLoading && <div className="animate-pulse space-y-2"><div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" /></div>}
              {leaderboardError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">{leaderboardError}</div>}
              {!leaderboardLoading && !leaderboardError && leaderboard.length === 0 && (
                <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] px-6 py-8 text-center">
                  <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No leaderboard data. The contest must be active and a calculation must have run.</p>
                </div>
              )}
              {leaderboard.length > 0 && (
                <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
                  <table className="min-w-full border-collapse">
                    <thead>
                      <tr>
                        <th className={thClass}>Rank</th>
                        <th className={thClass}>Position</th>
                        <th className={thClass}>Metric Value</th>
                        <th className={thClass}>Last Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaderboard.map((row, i) => (
                        <tr key={row.leaderboard_id ?? i} className="hover:bg-[rgb(var(--color-surface-muted))]">
                          <td className={tdClass + ' font-medium'}>{row.rank ?? i + 1}</td>
                          <td className={tdClass}>{row.position_id ?? '—'}</td>
                          <td className={tdClass}>{row.metric_value != null ? Number(row.metric_value).toLocaleString() : '—'}</td>
                          <td className={tdClass}>{row.updated_at ? new Date(row.updated_at).toLocaleDateString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Award Results tab (read-only) ── */}
      {activeTab === 'award-results' && (
        <div className="flex flex-col gap-4">
          <ReadOnlyNote />
          {!selectedContest ? <NoContestSelected /> : (
            <>
              <h2 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Award Results — {selectedContest.contest_name}</h2>
              {awardResultsLoading && <div className="animate-pulse space-y-2"><div className="h-8 rounded bg-[rgb(var(--color-surface-muted))]" /></div>}
              {awardResultsError && <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">{awardResultsError}</div>}
              {!awardResultsLoading && !awardResultsError && awardResults.length === 0 && (
                <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] px-6 py-8 text-center">
                  <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No award results yet. Contest must be in 'awarded' status.</p>
                </div>
              )}
              {awardResults.length > 0 && (
                <div className="overflow-x-auto rounded-xl border border-[rgb(var(--color-border))]">
                  <table className="min-w-full border-collapse">
                    <thead>
                      <tr>
                        <th className={thClass}>Rank</th>
                        <th className={thClass}>Position</th>
                        <th className={thClass}>Prize</th>
                        <th className={thClass}>Prize Value</th>
                        <th className={thClass}>Awarded At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {awardResults.map((r, i) => (
                        <tr key={r.award_result_id ?? i} className="hover:bg-[rgb(var(--color-surface-muted))]">
                          <td className={tdClass + ' font-medium'}>{r.rank != null ? `#${r.rank}` : '—'}</td>
                          <td className={tdClass}>{r.position_id ?? '—'}</td>
                          <td className={tdClass}>{r.prize_name ?? '—'}</td>
                          <td className={tdClass}>{r.prize_value != null ? Number(r.prize_value).toLocaleString() : '—'}</td>
                          <td className={tdClass}>{r.awarded_at ? new Date(r.awarded_at).toLocaleDateString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {toast && (
        <div role="status" className="fixed bottom-4 right-4 z-50 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3 text-tok-sm text-[rgb(var(--color-text))] shadow-lg">{toast}</div>
      )}
    </section>
  );
}
